/**
 * Greenhouse equipment the player buys: OWNERSHIP + UPGRADE TIERS. Today that is one kind, the compost
 * machine — a fixture standing inside the greenhouse in the bay under the wall terminal (its world
 * position is owned by RackLayer: COMPOST_X / COMPOST_BASE_Y). Counts are capped and persisted, and the
 * tier ladder below is read by the scene and the detail modal (`compostTier`).
 *
 * The solar panels and home battery that used to live here were REMOVED: the greenhouse runs off the
 * grid pole alone (see `sim/power.ts`). A loaded save's `equipment` entries for them are stripped in
 * `sim/save.ts` (`coerceEquipment`); nothing is refunded.
 */

export type EquipmentId = "compostMachine";

export type EquipmentKind = "compost";

export interface EquipmentDef {
  id: EquipmentId;
  kind: EquipmentKind;
  name: string;
  icon: string;
  price: number;
  /** Player LEVEL required to purchase this (progression gate). */
  unlockLevel: number;
  /** Hard cap on how many the player may own. */
  max: number;
}

export const EQUIPMENT: Record<EquipmentId, EquipmentDef> = {
  compostMachine: {
    id: "compostMachine",
    kind: "compost",
    name: "Composter",
    icon: "🌱",
    price: 260,
    unlockLevel: 12,
    max: 1,                // one compost machine (upgradeable in place)
  },
};

export const EQUIPMENT_IDS: EquipmentId[] = ["compostMachine"];

export function equipment(id: EquipmentId): EquipmentDef {
  return EQUIPMENT[id];
}

// ---- COMPOST MACHINE TIERS --------------------------------------------------
// The compost machine upgrades in place through larger, more detailed units. Each tier is a bigger scrap
// tank and finished-compost shelf, a faster batch and better rank odds (COMPOSTER_LEVELS in sim/composter.ts).
// `throughput` is unused flavour data.
export interface CompostTier {
  /** Tier index (0 = base). */
  tier: number;
  name: string;
  icon: string;
  /** Flavour throughput (kg/day of compost). Not shown anywhere; kept for the tier table. */
  throughput: number;
  /** Coins to upgrade FROM the previous tier TO this one. 0 for the base. */
  upgradePrice: number;
  blurb: string;
}

export const COMPOST_TIERS: CompostTier[] = [
  { tier: 0, name: "Compost Bin",       icon: "🌱", throughput: 2,  upgradePrice: 0,    blurb: "A simple slatted wooden bin that turns clippings into rich compost." },
  { tier: 1, name: "Tumbler Composter", icon: "🌱", throughput: 5,  upgradePrice: 300,  blurb: "A rotating drum on a frame that makes compost faster." },
  { tier: 2, name: "Hot Composter",     icon: "♻️", throughput: 10, upgradePrice: 620,  blurb: "A powered bin with a warm, steaming vent." },
  { tier: 3, name: "Bioreactor",        icon: "♻️", throughput: 18, upgradePrice: 1150, blurb: "A sealed, heated tank for hot composting." },
  { tier: 4, name: "Soil Synthesizer",  icon: "✨", throughput: 30, upgradePrice: 2000, blurb: "A high-tech unit with a glowing core that brews premium living soil." },
];

export const COMPOST_MAX_TIER = COMPOST_TIERS.length - 1;

export function compostTier(tier: number): CompostTier {
  return COMPOST_TIERS[Math.max(0, Math.min(COMPOST_MAX_TIER, Math.round(tier)))]!;
}
export function nextCompostTier(tier: number): CompostTier | null {
  return tier < COMPOST_MAX_TIER ? COMPOST_TIERS[tier + 1]! : null;
}
